// src/pages/ProductDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardHeader from '../components/DashboardHeader';
import AuthPromptModal from '../components/AuthPromptModal';
import ImageCarousel from '../components/ImageCarousel';
import Toast from '../components/Toast';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [stock, setStock] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('description');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [user, setUser] = useState(null);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(true);

  // Handle responsive breakpoint
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_API}/api/products/${id}`)
      .then(res => res.json())
      .then(data => {
        setProduct(data);
        setStock(data.stock);
        setReviews(data.reviews || []);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching product:', err);
        setIsLoading(false);
      });
  }, [id]);

  // Check if user is logged in and has purchased the product
  useEffect(() => {
    const checkUserEligibility = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setCheckingEligibility(false);
        return;
      }

      try {
        // Get user info
        const userResponse = await fetch(`${process.env.REACT_APP_API}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData.user || userData);
        }

        // Check if user has purchased this product
        const ordersResponse = await fetch(`${process.env.REACT_APP_API}/api/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (ordersResponse.ok) {
          const orders = await ordersResponse.json();
          const purchased = orders.some(order => {
            // Accept Completed or any successful order status
            const validStatuses = ['Completed', 'completed'];
            const hasValidStatus = validStatuses.includes(order.status);
            const hasProduct = order.items?.some(item => {
              const productId = item.product?._id || item.product;
              return productId === id;
            });
            return hasValidStatus && hasProduct;
          });
          setHasPurchased(purchased);
        }
      } catch (err) {
        console.error('Error checking eligibility:', err);
      } finally {
        setCheckingEligibility(false);
      }
    };

    checkUserEligibility();
  }, [id]);

  const role = localStorage.getItem('role');
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  // Redirect admins to product management immediately
  useEffect(() => {
    if (role === 'admin' || isAdmin) {
      navigate('/admin/products', { replace: true });
    }
  }, [role, isAdmin, navigate]);

  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const handleAddToCart = async () => {
    if (role === 'admin' || isAdmin) {
      alert('Admins cannot add items to the cart.');
      return;
    }
    if (quantity > (stock || 0)) {
      alert(`Cannot add more than ${stock} items. Please adjust your quantity.`);
      return;
    }
    // Calculate discounted price
    let discountedPrice = product.price;
    let hasDiscount = product.discount && Number(product.discount) > 0;
    if (hasDiscount) {
      discountedPrice = (Number(product.price) - (Number(product.price) * (Number(product.discount) / 100))).toFixed(2);
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // Show a friendly prompt with option to login or go back
        setShowAuthPrompt(true);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API}/api/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ productId: product._id, quantity, price: discountedPrice }),
      });

      if (!response.ok) throw new Error('Failed to add item');

      setToastMessage(`${product.name} added to cart!`);
      setShowToast(true);
      // Dispatch cart update event
      window.dispatchEvent(new Event('cartUpdated'));
      // Navigate after a short delay to let user see the toast
      setTimeout(() => navigate('/cart'), 1500);
    } catch (err) {
      console.error('Failed to add to cart', err);
      setToastMessage('There was a problem adding the item to the cart.');
      setShowToast(true);
    }
  };

  // close modal and optionally navigate to login
  const onAuthPromptClose = () => setShowAuthPrompt(false);

  // Submit review function
  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) {
      setToastMessage('Please write a review comment');
      setShowToast(true);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setShowAuthPrompt(true);
      return;
    }

    setIsSubmittingReview(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/products/${id}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          rating: newReview.rating,
          comment: newReview.comment
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit review');
      }

      setReviews(data.reviews || []);
      setNewReview({ rating: 5, comment: '' });
      setShowReviewForm(false);
      setToastMessage('Review submitted successfully!');
      setShowToast(true);

      // Update product with new review stats
      if (product) {
        setProduct({
          ...product,
          reviews: data.reviews,
          averageRating: data.averageRating,
          reviewCount: data.reviewCount
        });
      }
    } catch (err) {
      console.error('Failed to submit review:', err);
      setToastMessage(err.message || 'Failed to submit review. Please try again.');
      setShowToast(true);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Format date helper
  const formatReviewDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
        {/* Green Header Bar - Static */}
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
        {/* Navbar - Static */}
        <div style={{
          width: '100%',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          position: 'sticky',
          top: 0,
          zIndex: 9998,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
            <DashboardHeader />
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
          <div style={{ textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1, rotate: 360 }}
              transition={{ duration: 0.6, rotate: { duration: 1, repeat: Infinity, ease: 'linear' } }}
              style={{ fontSize: '4rem', marginBottom: '20px' }}
            >
              🌱
            </motion.div>
            <p style={{ fontSize: '1.1rem', color: '#64748b', fontWeight: 500 }}>Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return <div>Product not found</div>;

  // Calculate discounted price
  let discountedPrice = product.price;
  let hasDiscount = product.discount && Number(product.discount) > 0;
  if (hasDiscount) {
    discountedPrice = (Number(product.price) - (Number(product.price) * (Number(product.discount) / 100))).toFixed(2);
  }

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar - Static */}
      <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
        {/* Empty green bar for accent */}
      </div>
      {/* Navbar - Static */}
      <div style={{
        width: '100%',
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
        position: 'sticky',
        top: 0,
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>

      <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '20px 16px' : '40px 24px', flex: 1 }}>
        <motion.div 
          className="product-detail-layout"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div 
            className="product-info-section"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h1 className="product-name">{product.name}</h1>
            {/* Stock Badge */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}
            >
              <div className="stock-badge" data-in-stock={stock > 0}>
                {stock > 0 ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '6px' }}>
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {stock} in stock
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '6px' }}>
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5 5l6 6M11 5l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Out of stock
                  </>
                )}
              </div>
              
              {/* Amount Sold Badge */}
              {product.salesCount > 0 && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  background: '#fef3c7',
                  color: '#92400e',
                  border: '1.5px solid #fbbf24'
                }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '6px' }}>
                    <path d="M2 2L3.5 2L4.5 11H13L14 5H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="5" cy="14" r="1" fill="currentColor"/>
                    <circle cx="12" cy="14" r="1" fill="currentColor"/>
                  </svg>
                  {product.salesCount} sold
                </div>
              )}
            </motion.div>

            <motion.div 
              className="product-price-section"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              {hasDiscount ? (
                <>
                  <span className="price-original">₱{Number(product.price).toFixed(2)}</span>
                  <span className="price-discounted">₱{discountedPrice}</span>
                </>
              ) : (
                <span className="price-current">₱{Number(product.price).toFixed(2)}</span>
              )}
            </motion.div>

            <motion.div 
              className="description-tabs"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.6 }}
            >
              <div 
                className={`tab ${activeTab === 'description' ? 'active' : ''}`}
                onClick={() => setActiveTab('description')}
                style={{ cursor: 'pointer' }}
              >
                Description
              </div>
              <div 
                className={`tab ${activeTab === 'careGuide' ? 'active' : ''}`}
                onClick={() => setActiveTab('careGuide')}
                style={{ cursor: 'pointer' }}
              >
                Care Guide
              </div>
              <div 
                className={`tab ${activeTab === 'category' ? 'active' : ''}`}
                onClick={() => setActiveTab('category')}
                style={{ cursor: 'pointer' }}
              >
                Category
              </div>
            </motion.div>

            <motion.div 
              className="description-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              {activeTab === 'description' ? (
                <p>{product.description || 'No description available.'}</p>
              ) : activeTab === 'careGuide' ? (
                <p>{product.careGuide || 'No care guide available.'}</p>
              ) : (
                <div>
                  {product.tags && product.tags.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {product.tags.map((tag, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: '#2d5016',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '14px'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>{product.category || 'No category available.'}</p>
                  )}
                </div>
              )}
            </motion.div>

            <motion.div 
              className="quantity-cart-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.8 }}
            >
              <div className="quantity-control">
                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="qty-btn-circle" 
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                  disabled={quantity <= 1}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </motion.button>
                <input
                  type="text"
                  className="qty-input-display"
                  value={quantity}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, '');
                    let num = parseInt(val, 10);
                    if (isNaN(num) || num < 1) num = 1;
                    if (stock && num > stock) num = stock;
                    setQuantity(num);
                  }}
                  aria-label="Quantity"
                />
                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="qty-btn-circle" 
                  onClick={() => setQuantity(q => (stock ? Math.min(stock, q + 1) : q + 1))}
                  aria-label="Increase quantity"
                  disabled={quantity >= stock}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="8" y1="4" x2="8" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </motion.button>
              </div>
              <motion.button 
                whileHover={{ scale: stock > 0 && role !== 'admin' && !isAdmin ? 1.02 : 1 }}
                whileTap={{ scale: stock > 0 && role !== 'admin' && !isAdmin ? 0.98 : 1 }}
                className="add-to-cart-button" 
                onClick={handleAddToCart} 
                disabled={role === 'admin' || isAdmin || stock === 0}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-1.5 4.5M17 13l1.5 4.5M9 17.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0zM18 17.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {stock === 0 ? 'Out of Stock' : 'Add to Cart'}
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div 
            className="product-image-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ImageCarousel 
              images={[
                product.image,  // Main image is always first
                ...(product.images || [])  // Then additional carousel images
              ]} 
            />
          </motion.div>
        </motion.div>

        {/* Reviews Section */}
        <motion.div
          className="reviews-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9 }}
          style={{
            marginTop: isMobile ? '40px' : '60px',
            background: '#fff',
            borderRadius: '20px',
            padding: isMobile ? '24px 20px' : '40px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e8f4e8'
          }}
        >
          {/* Reviews Header */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '32px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <h2 style={{ 
                fontSize: isMobile ? '1.5rem' : '1.8rem', 
                fontWeight: 700, 
                color: '#1a5c2e',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '1.8rem' }}>🌿</span>
                Customer Reviews
              </h2>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <svg key={rating} width="20" height="20" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
                      {/* Base heart shape */}
                      <path
                        d="M50,90 L20,50 Q10,30 20,20 Q30,10 40,20 L50,30 L60,20 Q70,10 80,20 Q90,30 80,50 Z"
                        fill={rating <= Math.round(product?.averageRating || 0) ? '#5a9a5a' : '#e2e8f0'}
                      />
                      {/* Left leaf petal - darker green */}
                      <path
                        d="M50,30 Q25,25 20,50 L50,90 Z"
                        fill={rating <= Math.round(product?.averageRating || 0) ? '#4a8a4a' : '#d1d5db'}
                        opacity="0.9"
                      />
                      {/* Right leaf petal - lighter green */}
                      <path
                        d="M50,30 Q75,25 80,50 L50,90 Z"
                        fill={rating <= Math.round(product?.averageRating || 0) ? '#7ab87a' : '#e5e7eb'}
                        opacity="0.9"
                      />
                      {/* Center leaf petal - bright green */}
                      <path
                        d="M50,30 L50,15 Q52,8 50,5 Q48,8 50,15 L50,90 Z"
                        fill={rating <= Math.round(product?.averageRating || 0) ? '#8bc34a' : '#e8e8e8'}
                        opacity="0.95"
                      />
                    </svg>
                  ))}
                </div>
                <span style={{ color: '#64748b', fontSize: '0.95rem', fontWeight: 500 }}>
                  {product?.averageRating ? product.averageRating.toFixed(1) : '0.0'} out of 5 ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            </div>
            
            {!checkingEligibility && !showReviewForm && (
              !user ? (
                <div style={{
                  width: '100%',
                  padding: '16px 20px',
                  background: '#fff4e6',
                  border: '1px solid #ffd699',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap'
                }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM10 6v4M10 14h.01" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div style={{ flex: '1 1 200px' }}>
                    <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem', fontWeight: 500 }}>Sign in to write a review</p>
                    <p style={{ margin: '4px 0 0', color: '#b45309', fontSize: '0.8rem' }}>You must be logged in to share your experience</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/login')}
                    style={{
                      padding: '8px 16px',
                      background: '#d97706',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Sign In
                  </motion.button>
                </div>
              ) : !hasPurchased ? (
                <div style={{
                  width: '100%',
                  padding: '16px 20px',
                  background: '#fef3f2',
                  border: '1px solid #fecdd3',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM10 6v4M10 14h.01" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, color: '#991b1b', fontSize: '0.9rem', fontWeight: 500 }}>Purchase required to review</p>
                    <p style={{ margin: '4px 0 0', color: '#b91c1c', fontSize: '0.8rem' }}>Only verified buyers can write reviews for this product</p>
                  </div>
                </div>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowReviewForm(true)}
                  style={{
                    padding: isMobile ? '12px 20px' : '14px 28px',
                    background: '#2d7a45',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 2px 8px rgba(45, 122, 69, 0.2)'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 3v14M3 10h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Write a Review
                </motion.button>
              )
            )}
          </div>

          {/* Review Form - Only show to eligible users */}
          {showReviewForm && user && hasPurchased && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                background: '#f8fcf9',
                borderRadius: '16px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: '32px',
                border: '2px solid #d1f0d9'
              }}
            >
              <h3 style={{ 
                fontSize: '1.2rem', 
                fontWeight: 600, 
                color: '#1a5c2e',
                marginBottom: '20px'
              }}>
                Share Your Experience
              </h3>
              
              {/* Star Rating */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.9rem', 
                  fontWeight: 500, 
                  color: '#475569',
                  marginBottom: '8px'
                }}>
                  Your Rating
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <motion.button
                      key={rating}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setNewReview({ ...newReview, rating })}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <svg width="32" height="32" viewBox="0 0 100 100">
                        {/* Base heart shape */}
                        <path
                          d="M50,90 L20,50 Q10,30 20,20 Q30,10 40,20 L50,30 L60,20 Q70,10 80,20 Q90,30 80,50 Z"
                          fill={rating <= newReview.rating ? '#5a9a5a' : '#e2e8f0'}
                        />
                        {/* Left leaf petal - darker green */}
                        <path
                          d="M50,30 Q25,25 20,50 L50,90 Z"
                          fill={rating <= newReview.rating ? '#4a8a4a' : '#d1d5db'}
                          opacity="0.9"
                        />
                        {/* Right leaf petal - lighter green */}
                        <path
                          d="M50,30 Q75,25 80,50 L50,90 Z"
                          fill={rating <= newReview.rating ? '#7ab87a' : '#e5e7eb'}
                          opacity="0.9"
                        />
                        {/* Center leaf petal - bright green */}
                        <path
                          d="M50,30 L50,15 Q52,8 50,5 Q48,8 50,15 L50,90 Z"
                          fill={rating <= newReview.rating ? '#8bc34a' : '#e8e8e8'}
                          opacity="0.95"
                        />
                      </svg>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Comment Textarea */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.9rem', 
                  fontWeight: 500, 
                  color: '#475569',
                  marginBottom: '8px'
                }}>
                  Your Review
                </label>
                <textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                  placeholder="Tell us what you think about this product..."
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '14px',
                    border: '2px solid #d1f0d9',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2d7a45'}
                  onBlur={(e) => e.target.style.borderColor = '#d1f0d9'}
                />
              </div>

              {/* Form Buttons */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    handleSubmitReview();
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#2d7a45',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: newReview.comment.trim() && !isSubmittingReview ? 'pointer' : 'not-allowed',
                    opacity: newReview.comment.trim() && !isSubmittingReview ? 1 : 0.5,
                    flex: isMobile ? '1' : '0'
                  }}
                  disabled={!newReview.comment.trim() || isSubmittingReview}
                >
                  {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowReviewForm(false);
                    setNewReview({ rating: 5, comment: '' });
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#fff',
                    color: '#64748b',
                    border: '2px solid #e2e8f0',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    flex: isMobile ? '1' : '0'
                  }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Reviews List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {reviews.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#94a3b8'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🌱</div>
                <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '8px' }}>No reviews yet</p>
                <p style={{ fontSize: '0.9rem' }}>Be the first to share your experience with this product!</p>
              </div>
            ) : (
              reviews.map((review, index) => (
                <motion.div
                  key={review._id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  style={{
                    background: '#fafcfa',
                    borderRadius: '16px',
                    padding: isMobile ? '20px' : '24px',
                    border: '1px solid #e8f4e8',
                    position: 'relative'
                  }}
                >
                  {/* Review Header */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        marginBottom: '6px'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #d1f0d9 0%, #a7e3b8 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          color: '#1a5c2e'
                        }}>
                          {review.userName ? review.userName.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px'
                          }}>
                            <span style={{ 
                              fontWeight: 600, 
                              color: '#1e293b',
                              fontSize: '0.95rem'
                            }}>
                              {review.userName || 'Anonymous'}
                            </span>
                            {review.verified && (
                              <span style={{
                                background: '#d1f0d9',
                                color: '#1a5c2e',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                padding: '3px 8px',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Verified Buyer
                              </span>
                            )}
                          </div>
                          <span style={{ 
                            fontSize: '0.8rem', 
                            color: '#94a3b8'
                          }}>
                            {formatReviewDate(review.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Leaf Heart Rating */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <svg key={rating} width="18" height="18" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
                          {/* Base heart shape */}
                          <path
                            d="M50,90 L20,50 Q10,30 20,20 Q30,10 40,20 L50,30 L60,20 Q70,10 80,20 Q90,30 80,50 Z"
                            fill={rating <= review.rating ? '#5a9a5a' : '#e2e8f0'}
                          />
                          {/* Left leaf petal - darker green */}
                          <path
                            d="M50,30 Q25,25 20,50 L50,90 Z"
                            fill={rating <= review.rating ? '#4a8a4a' : '#d1d5db'}
                            opacity="0.9"
                          />
                          {/* Right leaf petal - lighter green */}
                          <path
                            d="M50,30 Q75,25 80,50 L50,90 Z"
                            fill={rating <= review.rating ? '#7ab87a' : '#e5e7eb'}
                            opacity="0.9"
                          />
                          {/* Center leaf petal - bright green */}
                          <path
                            d="M50,30 L50,15 Q52,8 50,5 Q48,8 50,15 L50,90 Z"
                            fill={rating <= review.rating ? '#8bc34a' : '#e8e8e8'}
                            opacity="0.95"
                          />
                        </svg>
                      ))}
                    </div>
                  </div>

                  {/* Review Comment */}
                  <p style={{ 
                    color: '#475569',
                    lineHeight: '1.6',
                    fontSize: '0.95rem',
                    margin: 0
                  }}>
                    {review.comment}
                  </p>
                </motion.div>
              ))
            )}
          </div>

          {/* Load More Button - Only show if there are many reviews */}
          {reviews.length >= 10 && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                width: '100%',
                padding: '14px',
                background: '#fff',
                color: '#2d7a45',
                border: '2px solid #2d7a45',
                borderRadius: '12px',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                marginTop: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              Load More Reviews
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </motion.button>
          )}
        </motion.div>
      </div>

      {/* Auth Prompt Modal */}
      <AuthPromptModal open={showAuthPrompt} onClose={onAuthPromptClose} />
      {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} />}
    </div>
  );
}

export default ProductDetail;
